import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
import { devices } from 'src/stores/lists/devices';
import { is_loqed } from './interface_helpers';
import { loqedPendingOperationsDevices } from 'src/stores/integrations/loqed';
import { getBatteryIconStyle } from './device_data_helpers';

/**
 * Function to handle device status update for Loqed devices
 * @param status Device status
 * @param deviceId Device id
 * @returns Updated status
 */
export const handleLoqedStatusUpdate = (status: any, deviceId: string) => {
	// workaround for the first invalid status after going online
	if (
		status?.battery_percentage === -1 &&
		typeof status?._preoffline_battery_percentage !== 'undefined' &&
		typeof status?._preoffline_bolt_state !== 'undefined'
	) {
		status.battery_percentage = status._preoffline_battery_percentage;
		status.bolt_state = status._preoffline_bolt_state;
	}

	// remove the id from the Loqed pending operations list on status event
	if (typeof get(loqedPendingOperationsDevices)[deviceId] !== 'undefined') {
		loqedPendingOperationsDevices.remove(deviceId);
	}

	return status;
};

/**
 * Helper to get the number ot Loqed devices
 * @returns Number of Loqed devices
 */
export const getLoqedDevicesNumber = () => {
	const allDevices = get(devices);
	return allDevices ? Object.values(allDevices).filter((d) => is_loqed(d))?.length : 0;
};

/**
 * Convert Loqed bolt_state to Shelly convention
 * @param _state State in Loqed format (night_lock, day_lock, open)
 * @returns State in Shelly format (locked, unlocked, open)
 */
export const loqedToShelly = (_state: string) => {
	switch (_state) {
		case 'night_lock':
			return 'locked';
		case 'day_lock':
			return 'unlocked';
		default:
			return _state;
	}
};

export const loqedStatusConfigurator = (status: any) => {
	const _format = get(format);
	let interfaceData = [
		{
			name: 'battery',
			icon: 'fa-duotone fa-light fa-battery-full',
			value: `${status?.battery_percentage || 0} % `,
			iconStyle: getBatteryIconStyle(status?.battery_percentage),
		},
	] as {
		name: string;
		translation?: string;
		icon: string;
		value: string;
		iconStyle?: string;
		iconColor?: string;
	}[];

	switch (status?.bolt_state) {
		case 'night_lock':
			interfaceData.push({
				name: 'state',
				translation: _format('loqed_night_lock_state', {
					default: 'Current state',
				}),
				icon: 'fas fa-lock',
				value: _format(`condition_operator_bolt_state_night_lock`),
				iconColor: '--color-danger',
			});
			break;
		case 'day_lock':
			interfaceData.push({
				name: 'state',
				translation: _format('loqed_night_lock_state', {
					default: 'Current state',
				}),
				icon: 'fas fa-unlock',
				value: _format(`condition_operator_bolt_state_day_lock`),
				iconColor: '--color-success',
			});
			break;
		case 'open':
			interfaceData.push({
				name: 'state',
				translation: _format('loqed_night_lock_state', {
					default: 'Current state',
				}),
				icon: 'fas fa-door-open',
				value: _format(`condition_operator_bolt_state_open`),
				iconColor: '--color-primary',
			});
			break;
		case 'unknown':
		default:
			interfaceData.push({
				name: 'state',
				translation: _format('loqed_night_lock_state', {
					default: 'Current state',
				}),
				icon: 'fas fa-circle-question',
				value: 'Unknown',
				iconColor: '--color-muted',
			});
			break;
	}

	return interfaceData;
};
